import React, {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from "react";

import styled from "styled-components";
import { Col, Form, Pagination, Row, Spinner, Table } from "react-bootstrap";
import { useSticky } from "react-table-sticky";
import {
    useTable,
    usePagination,
    useRowSelect,
    useExpanded,
    useMountedLayoutEffect,
    useGlobalFilter,
    // useBlockLayout,
    useSortBy,
} from "react-table";

const DynamicTable = (props) => {
    const {
        columns,
        dataPagination,
        api,
        className,
        withCheckbox,
        hiddenColumns,
        singleSelectCheckbox,
        setSelectedRows = false,
        isPaginated = true,
        isFullPage = true,
        isStriped = true,
        isExpanded = false,
        updateData = null,
        updateSort = null,
        canViewRow = null,
        canSignRow = null,
        defaultSelectedRows,
        getRowId,
        onChangeSelectedRowsId = false,
        toDeleteRow = false,
        isLoading = false,
        footerRow = null,
        canSelectRow = () => true,
    } = props;

    const scrollRef = useRef(null);
    const scrollPositionRef = useRef(0);

    const { data, meta, links } = dataPagination;

    const IndeterminateCheckbox = memo(
        forwardRef(({ indeterminate, ...rest }, ref) => {
            const defaultRef = useRef();
            const resolvedRef = ref || defaultRef;

            useEffect(() => {
                resolvedRef.current.indeterminate = indeterminate;
            }, [resolvedRef, indeterminate]);

            return (
                <>
                    <Form.Check type="checkbox" ref={resolvedRef} {...rest} />
                </>
            );
        })
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        toggleAllRowsExpanded,
        isAllRowsExpanded,
        selectedFlatRows,
        state: {
            pageIndex,
            apiNextPage,
            apiPreviousPage,
            firstPage,
            lastPage,
            selectedRowIds,
        },
        toggleRowSelected,
        toggleAllRowsSelected,
    } = useTable(
        {
            columns: columns,
            data: data,
            getRowId,
            initialState: {
                pageIndex: 0,
                hiddenColumns: hiddenColumns ? hiddenColumns : [],
                autoResetExpanded: false,
                apiNextPage: links?.next !== null ? true : false,
                apiPreviousPage: links?.prev !== null ? true : false,
                pageSize: meta?.total ? meta?.total : 200,
                totalPage: meta?.last_page,
                currentPage: meta?.current_page,
                firstPage: meta?.first,
                lastPage: meta?.last_page,
                selectedRowIds: defaultSelectedRows || {},
            },
            stateReducer: singleSelectCheckbox
                ? (newState, action) => {
                      if (action.type === "toggleRowSelected") {
                          newState.selectedRowIds = {
                              [action.id]: true,
                          };
                      }

                      return newState;
                  }
                : (newState) => newState,
            updateData: updateData,
            updateSort: updateSort,
            canViewRow: canViewRow,
            canSignRow: canSignRow,
        },
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
        // useBlockLayout,
        useSticky,
        (hooks) => {
            if (withCheckbox) {
                hooks.visibleColumns.push((columns) => [
                    // Let's make a column for selection
                    {
                        id: "selection",
                        // The header can use the table's getToggleAllRowsSelectedProps method
                        // to render a checkbox
                        Header: ({ page, toggleRowSelected }) => {
                            if (singleSelectCheckbox) return null;

                            const selectableRows = page.filter((row) =>
                                canSelectRow(row.original)
                            );
                            const selectedCount = selectableRows.filter(
                                (row) => row.isSelected
                            ).length;

                            const allSelected =
                                selectableRows.length > 0 &&
                                selectedCount === selectableRows.length;
                            const someSelected =
                                selectedCount > 0 && !allSelected;

                            const handleChange = () => {
                                const nextState = !allSelected;
                                selectableRows.forEach((row) =>
                                    toggleRowSelected(row.id, nextState)
                                );
                            };

                            return (
                                <div>
                                    <IndeterminateCheckbox
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        disabled={selectableRows.length === 0}
                                        onChange={handleChange}
                                    />
                                </div>
                            );
                        },
                        // The cell can use the individual row's getToggleRowSelectedProps method
                        // to the render a checkbox
                        Cell: ({ row }) => {
                            return (
                                row.depth === 0 && (
                                    <div>
                                        <IndeterminateCheckbox
                                            {...row.getToggleRowSelectedProps({
                                                disabled: !canSelectRow(
                                                    row.original
                                                ),
                                            })}
                                        />
                                    </div>
                                )
                            );
                        },
                        width: 40,
                        // sticky: "left",
                    },
                    ...columns,
                ]);
            }
        }
    );

    const gotoPageApi = (page) => {
        api({
            page,
        });
    };

    const deselectSelectedRow = (id) => {
        const rowToDelete = page.find((data) => {
            return data.original.id === id;
        });

        rowToDelete && toggleRowSelected(rowToDelete.id, false);
    };

    const deselectAllRows = () => {
        toggleAllRowsSelected(false);
    };

    const handleScroll = useCallback(() => {
        scrollPositionRef.current = scrollRef.current?.scrollTop || 0;
    }, []);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollPositionRef.current;
        }
    });

    useEffect(() => {
        if (isExpanded) toggleAllRowsExpanded(true);
    }, [isAllRowsExpanded, isExpanded, toggleAllRowsExpanded]);

    useEffect(() => {
        if (setSelectedRows) {
            const mappedSelectedFlatRows = selectedFlatRows.map(
                (row) => row.original
            );

            setSelectedRows(mappedSelectedFlatRows);
        }
    }, [setSelectedRows, selectedFlatRows]);

    useEffect(() => {
        if (toDeleteRow === "ALL") {
            deselectAllRows();
            return;
        }

        if (toDeleteRow.length) {
            deselectSelectedRow(toDeleteRow);
        }
    }, [toDeleteRow]);

    useMountedLayoutEffect(() => {
        onChangeSelectedRowsId && onChangeSelectedRowsId(selectedRowIds);
    }, [selectedRowIds]);

    const StylesRef = useRef(styled.div`
        .table-wrapper {
            height: calc(100vh - 315px);
            overflow: auto;

            scrollbar-width: thin;
            scrollbar-color: transparent transparent;
        }

        .table-wrapper::-webkit-scrollbar {
            width: 8px;
            height: 8px;
        }

        .table-wrapper::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 10px;
        }

        .table-wrapper:hover::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.3);
        }

        .table-wrapper:hover {
            scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
        }
        .table {
            table-layout: fixed;

            .th,
            .td {
                background-color: #fff;
                overflow: hidden;
                border-top: none;
            }
        }
        .table > :not(:first-child) {
            border-top: none;
        }

        thead.header th.th {
            position: sticky;
            top: 0;
            z-index: 1;
            background-color: #fff !important;
            box-shadow: inset 0 1px 0 #dee2e6, inset 0 -1px 0 #dee2e6;
        }
    `);

    const Styles = StylesRef.current;

    return (
        <>
            {isPaginated && (
                <Row className="fixed-header-w-navbar py-2">
                    <Col className="d-flex justify-content-end align-items-center gap-3 ">
                        <span className="mx-2">
                            Page{" "}
                            <strong>
                                {api &&
                                    `${meta?.current_page || 1} of ${
                                        meta?.last_page || 1
                                    }`}
                                {!api &&
                                    `${pageIndex + 1} of  ${
                                        pageOptions.length
                                    }`}
                            </strong>
                        </span>
                        <Pagination className="float-end  mb-0">
                            {!api && (
                                <>
                                    {" "}
                                    <Pagination.First
                                        onClick={() => gotoPage(0)}
                                        disabled={!canPreviousPage}
                                    />
                                    <Pagination.Prev
                                        onClick={() => previousPage()}
                                        disabled={!canPreviousPage}
                                    />
                                    <Pagination.Next
                                        onClick={() => nextPage()}
                                        disabled={!canNextPage}
                                    />
                                    <Pagination.Last
                                        onClick={() =>
                                            gotoPageApi(meta?.last_page)
                                        }
                                        disabled={!apiNextPage}
                                    />
                                </>
                            )}

                            {api && (
                                <>
                                    <Pagination.First
                                        onClick={() => gotoPageApi(firstPage)}
                                        disabled={!apiPreviousPage}
                                    />
                                    <Pagination.Prev
                                        onClick={(i) =>
                                            gotoPageApi(meta?.current_page - 1)
                                        }
                                        disabled={!apiPreviousPage}
                                    />
                                    <Pagination.Next
                                        onClick={() =>
                                            gotoPageApi(meta?.current_page + 1)
                                        }
                                        disabled={!apiNextPage}
                                    />
                                    <Pagination.Last
                                        onClick={() =>
                                            gotoPageApi(meta?.last_page)
                                        }
                                        disabled={!apiNextPage}
                                    />
                                </>
                            )}
                        </Pagination>
                    </Col>
                </Row>
            )}

            <Styles>
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className={`${
                        isFullPage
                            ? "table-wrapper table-responsive w-100"
                            : "table-responsive w-100"
                    }`}
                >
                    <Table
                        striped={isStriped}
                        bordered
                        {...getTableProps()}
                        className={`w-100 ${className}`}
                    >
                        <thead className="header">
                            {headerGroups.map((headerGroup) => (
                                <tr
                                    {...headerGroup.getHeaderGroupProps()}
                                    className="tr"
                                >
                                    {headerGroup.headers.map((column) => (
                                        <th
                                            className="th"
                                            width={column.width}
                                            {...column.getHeaderProps({
                                                ...column.getSortByToggleProps(),
                                                style: { width: column.width },
                                            })}
                                        >
                                            {column.render("Header")}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {isLoading && (
                                <tr>
                                    <td
                                        colSpan={columns.length}
                                        className="ps-4 py-4"
                                    >
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                        Loading...
                                    </td>
                                </tr>
                            )}
                            {!isLoading && data && data.length < 1 && (
                                <tr>
                                    <td colSpan={columns.length}>
                                        No records found
                                    </td>
                                </tr>
                            )}
                            {!isLoading &&
                                page.map((row, i) => {
                                    prepareRow(row);
                                    return (
                                        <React.Fragment key={i}>
                                            {row.original.header ? (
                                                <tr className="tr">
                                                    <td
                                                        colSpan={columns.length}
                                                        className="header td"
                                                    >
                                                        {row.original.header}
                                                    </td>
                                                </tr>
                                            ) : (
                                                <tr {...row.getRowProps()}>
                                                    {row.cells.map(
                                                        (cell, index) => {
                                                            // Handle checkbox column
                                                            if (
                                                                cell.column
                                                                    .id ===
                                                                "selection"
                                                            ) {
                                                                return (
                                                                    <td
                                                                        {...cell.getCellProps(
                                                                            {
                                                                                style: {
                                                                                    width: cell
                                                                                        .column
                                                                                        .width,
                                                                                },
                                                                            }
                                                                        )}
                                                                        key={
                                                                            index
                                                                        }
                                                                        className="td"
                                                                    >
                                                                        {cell.render(
                                                                            "Cell"
                                                                        )}
                                                                    </td>
                                                                );
                                                            }

                                                            // Handle S.No column
                                                            if (
                                                                cell.column
                                                                    .id ===
                                                                "s_no"
                                                            ) {
                                                                return (
                                                                    <td
                                                                        {...cell.getCellProps(
                                                                            {
                                                                                style: {
                                                                                    width: cell
                                                                                        .column
                                                                                        .width,
                                                                                },
                                                                            }
                                                                        )}
                                                                        key={
                                                                            index
                                                                        }
                                                                        className="td"
                                                                    >
                                                                        {meta?.current_page &&
                                                                        meta?.per_page
                                                                            ? (meta?.current_page -
                                                                                  1) *
                                                                                  meta?.per_page +
                                                                              i +
                                                                              1
                                                                            : i +
                                                                              1}
                                                                    </td>
                                                                );
                                                            }

                                                            // All other columns
                                                            return (
                                                                <td
                                                                    className={`td ${
                                                                        cell
                                                                            .column
                                                                            .id ===
                                                                        "actions"
                                                                            ? "table-action"
                                                                            : ""
                                                                    }`}
                                                                    {...cell.getCellProps(
                                                                        {
                                                                            style: {
                                                                                width: cell
                                                                                    .column
                                                                                    .width,
                                                                            },
                                                                        }
                                                                    )}
                                                                >
                                                                    {cell.render(
                                                                        "Cell"
                                                                    )}
                                                                </td>
                                                            );
                                                        }
                                                    )}
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                        </tbody>
                        {footerRow && <tfoot>{footerRow}</tfoot>}
                    </Table>
                </div>
            </Styles>

            {isPaginated && (
                <Row className="fixed-header-w-navbar py-2">
                    <Col className="d-flex justify-content-end align-items-center gap-3">
                        <span className="tex-muted">
                            Page{" "}
                            <strong>
                                {api &&
                                    `${meta?.current_page || 1} of ${
                                        meta?.last_page || 1
                                    }`}
                                {!api &&
                                    `${pageIndex + 1} of  ${
                                        pageOptions.length
                                    }`}
                            </strong>
                        </span>
                        <Pagination className="float-end mb-0">
                            {!api && (
                                <>
                                    {" "}
                                    <Pagination.First
                                        onClick={() => gotoPage(0)}
                                        disabled={!canPreviousPage}
                                    />
                                    <Pagination.Prev
                                        onClick={() => previousPage()}
                                        disabled={!canPreviousPage}
                                    />
                                    <Pagination.Next
                                        onClick={() => nextPage()}
                                        disabled={!canNextPage}
                                    />
                                    <Pagination.Last
                                        onClick={() => gotoPage(pageCount - 1)}
                                        disabled={!canNextPage}
                                    />
                                </>
                            )}

                            {api && (
                                <>
                                    <Pagination.First
                                        onClick={() => gotoPageApi(firstPage)}
                                        disabled={!apiPreviousPage}
                                    />
                                    <Pagination.Prev
                                        onClick={(i) =>
                                            gotoPageApi(meta?.current_page - 1)
                                        }
                                        disabled={!apiPreviousPage}
                                    />
                                    <Pagination.Next
                                        onClick={() =>
                                            gotoPageApi(meta?.current_page + 1)
                                        }
                                        disabled={!apiNextPage}
                                    />
                                    <Pagination.Last
                                        onClick={() =>
                                            gotoPageApi(meta?.last_page)
                                        }
                                        disabled={!apiNextPage}
                                    />
                                </>
                            )}
                        </Pagination>
                    </Col>
                </Row>
            )}
        </>
    );
};
export default DynamicTable;
